import { FishingReport, SeasonGroup, Species, WaterbodyDetail } from '@omniafishing/core';
import classNames from 'classnames';
import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useReferenceData } from '../../../../hooks/use_reference_data';
import { useResponsive } from '../../../../hooks/use_responsive';
import { tally } from '../../../../lib/tally';
import SvgFishingReport from '../../../svg/fishing_report';
import SvgNewProductTransparent from '../../../svg/new_product_transparent';
import { TechniqueFullWithReportCount } from '../waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import styles from './waterbody_detail_stats.less';
import { OmniaButton } from '../../../omnia_button/omnia_button';

interface WaterbodyDetailStatsProps {
  fishingReports: FishingReport[];
  fishingReportsNearby: FishingReport[];
  onFishingReportsClick: () => void;
  onTechniqueClick: (t: TechniqueFullWithReportCount) => void;
  onSpeciesClick: (s: Species) => void;
  selectedSeasonGroup: SeasonGroup;
  selectedSpecies: Species;
  topTechniquesWithCounts: TechniqueFullWithReportCount[];
  waterbody: WaterbodyDetail;
}

export const WaterbodyDetailStats = (props: WaterbodyDetailStatsProps) => {
  const {
    fishingReports,
    fishingReportsNearby,
    onFishingReportsClick,
    onSpeciesClick,
    onTechniqueClick,
    selectedSeasonGroup,
    selectedSpecies,
    topTechniquesWithCounts,
    waterbody,
  } = props;

  const { isDesktop, isSmallDesktop } = useResponsive();
  const { allSpecies } = useReferenceData();

  const fishingReportsCount = fishingReports.length;
  const selectedSeasonNames = selectedSeasonGroup.seasons.map((s) => s.name);

  const pieData = topTechniquesWithCounts.map((technique) => {
    return {
      title: technique.display_name,
      value: technique.reportCount || 1, // this can be 0 from default waterbody techniques, but will render nothing if 0
      color: technique.color,
    };
  });
  const pieSlice = pieData.slice(0, 10);

  const hasReports = fishingReportsCount > 0;

  const seasonSpeciesNamesInReports = useMemo(() => {
    const seasonNames = selectedSeasonGroup.seasons.map((s) => s.name);
    const selectedSeasonSpeciesInReports = fishingReports
      .filter((report) => seasonNames.includes(report.season.name))
      .map((report) => report.species);
    return selectedSeasonSpeciesInReports.map((s) => s.name);
  }, [selectedSeasonGroup, fishingReports]);

  const seasonSpeciesTally = tally(seasonSpeciesNamesInReports);
  const seasonSpecies = allSpecies
    .filter((s) => seasonSpeciesNamesInReports.includes(s.name))
    .slice(0, 5);
  const seasonSpeciesOrdered = _.orderBy(seasonSpecies, (s) => seasonSpeciesTally[s.name], [
    'desc',
  ]);

  const nonSeasonSpeciesNames = fishingReports
    .filter((report) => !selectedSeasonNames.includes(report.season.name))
    .map((report) => report.species.name);
  const nonSeasonSpeciesTally = tally(nonSeasonSpeciesNames);
  const nonSeasonSpecies = allSpecies
    .filter((s) => nonSeasonSpeciesNames.includes(s.name))
    .slice(0, 5);
  const nonSeasonSpeciesOrdered = _.orderBy(
    nonSeasonSpecies,
    (s) => nonSeasonSpeciesTally[s.name],
    ['desc']
  );

  const nearbySeasonSpeciesNames = fishingReportsNearby
    .filter((report) => selectedSeasonNames.includes(report.season.name))
    .map((report) => report.species.name);
  const nearbySeasonSpeciesTally = tally(nearbySeasonSpeciesNames);
  const nearbySeasonSpecies = allSpecies
    .filter((s) => nearbySeasonSpeciesNames.includes(s.name))
    .slice(0, 5);
  const nearbySeasonSpeciesOrdered = _.orderBy(
    nearbySeasonSpecies,
    (s) => nearbySeasonSpeciesTally[s.name],
    ['desc']
  );

  return (
    <div>
      <div className={styles.statSectionContainer}>
        <div className={classNames(styles.stat, styles.stat__topTechniques)}>
          <p className={styles.statHeading__techniques}>
            {isSmallDesktop && <SvgNewProductTransparent />}
            Top Techniques {hasReports && isDesktop && 'Reported'} for{' '}
            {selectedSpecies.display_name} + {selectedSeasonGroup.display_name}
          </p>
          <div className={styles.statsTopTechniques}>
            {isDesktop && (
              <div className={styles.pie}>
                <PieChart
                  data={pieSlice}
                  radius={PieChart.defaultProps.radius - 6}
                  lineWidth={60}
                  startAngle={270}
                  animate
                />
              </div>
            )}
            <div className={styles.statsTechniques}>
              <ol className={styles.statTechniqueList}>
                {topTechniquesWithCounts.slice(0, 5).map((t) => {
                  return (
                    <li
                      key={t.name}
                      className={styles.statTechniqueItem}
                      onClick={() => {
                        onTechniqueClick(t);
                      }}
                    >
                      <div className={styles.techniqueNameColor}>
                        <span style={{ background: t.color }} className={styles.colorSwatch}></span>
                        <u>{t.display_name} </u>
                      </div>
                      <span>{t.reportCount > 0 && t.reportCount}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
        <div className={classNames(styles.stat, styles.stat__fishingReport)}>
          <span className={styles.statHeading}>
            <SvgFishingReport />
            Total Fishing Reports
          </span>
          <div>
            <span className={styles.statFishingReportsCount}>{fishingReportsCount} </span>
          </div>

          <div className={styles.statFooter}>
            <OmniaButton kind="primary" size="md" onPress={onFishingReportsClick}>
              View {hasReports ? 'Fishing' : 'Nearby'} Reports
            </OmniaButton>
          </div>
        </div>
        <div className={classNames(styles.stat, styles.stat__species)}>
          <span className={styles.statHeading}>
            Top Species Reported for {selectedSeasonGroup.display_name}
          </span>
          {seasonSpeciesOrdered.length > 0 ? (
            <div>
              <ol className={styles.speciesList}>
                {seasonSpeciesOrdered.map((s) => (
                  <li className={styles.speciesItem} key={s.name}>
                    <span className={styles.speciesName}>
                      <img src={s.image} alt={s.img_alt} className={styles.speciesImg} />
                      {s.display_name}
                    </span>
                    <span
                      className={styles.speciesLink}
                      onClick={() => {
                        onSpeciesClick(s);
                      }}
                    >
                      {seasonSpeciesTally[s.name]} {pluralize('Report', seasonSpeciesTally[s.name])}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className={styles.nonSeasonSpecies}>
              <p>
                <strong>0 reports</strong> for {selectedSeasonGroup.display_name}
              </p>

              {nonSeasonSpeciesOrdered.length > 0 ? (
                <>
                  <p>
                    {' '}
                    <strong>Other Species Reported for {waterbody.primary_name}</strong>
                  </p>
                  <ol className={styles.speciesList}>
                    {nonSeasonSpeciesOrdered.map((s) => (
                      <li className={styles.speciesItem} key={s.name}>
                        <span className={styles.speciesName}>
                          <img src={s.image} alt={s.img_alt} className={styles.speciesImg} />
                          {s.display_name}
                        </span>
                        <span
                          className={styles.speciesLink}
                          onClick={() => {
                            onSpeciesClick(s);
                          }}
                        >
                          {nonSeasonSpeciesTally[s.name]}{' '}
                          {pluralize('Report', nonSeasonSpeciesTally[s.name])}
                        </span>
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <>
                  <p>
                    {' '}
                    <strong>Other Species Reported Nearby</strong>
                  </p>
                  {nearbySeasonSpeciesOrdered.map((s) => (
                    <li className={styles.speciesItem} key={s.name}>
                      <span className={styles.speciesName}>
                        <img src={s.image} alt={s.img_alt} className={styles.speciesImg} />
                        {s.display_name}
                      </span>
                      <span
                        className={styles.speciesLink}
                        onClick={() => {
                          onSpeciesClick(s);
                        }}
                      >
                        {nonSeasonSpeciesTally[s.name]}{' '}
                        {pluralize('Report', nonSeasonSpeciesTally[s.name])}
                      </span>
                    </li>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
